'use client'; // needed to support use of 'onClick'

import { EnergyUsageOutput } from '@/lib/actions/usage/constants';
import { getDisplayNumber, getSign } from '@/lib/numbers';
import {
    addCleanHeatAndAcAtom,
    addHeatPumpWaterHeaterAtom,
    addHvacUseAtom,
    addInductionStoveAtom,
    addPeopleAtom,
    baseUsageAtom,
    basicInfoExpandAtom,
    evsAtom,
} from '@/ui/atoms/form-atoms';
import { useEnergyOffsetWithUpdates } from '@/ui/hooks/useEnergyOffset';
import { useResponsiveUi } from '@/ui/hooks/useResponsiveUi';
import { NoSSR } from '@/ui/utils/NoSSR';
import { Anchor, Loader } from '@mantine/core';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { AccessibleTooltip } from '../AccessibleTooltip';
import styles from './EnergyBar.module.css';

const SPACING = <>&nbsp; &nbsp;</>;

function adjustBarPcts(lowFr: number, highFr: number) {
    if (lowFr === 0 && highFr === 0) {
        return [0, 0] as const;
    }

    const highPct = 70; // empty bar needs to accomodate for future label
    const lowPct = (lowFr / highFr) * highPct;

    return [lowPct, highPct] as const;
}

export const EnergyBar: React.FC = () => {
    const setBasicInfoExpand = useSetAtom(basicInfoExpandAtom);
    const { inputComplete, loading, usageResult, errorMessage } =
        useEnergyOffsetWithUpdates();
    const { isMobile } = useResponsiveUi();
    const baseUsage = useAtomValue(baseUsageAtom);

    return (
        <EnergyBarView
            setBasicInfoExpand={setBasicInfoExpand}
            inputComplete={inputComplete}
            loading={loading}
            usageResult={usageResult}
            errorMessage={errorMessage}
            isMobile={isMobile}
            baseUsage={baseUsage}
        />
    );
};

// Jotai setter types are no fun.
type SetBasicInfoExpand =
    | ReturnType<typeof useSetAtom<typeof basicInfoExpandAtom>>
    | ((fn: (b: boolean) => boolean) => void);

export type EnergyBarViewProps = {
    setBasicInfoExpand: SetBasicInfoExpand;
    inputComplete: boolean;
    loading: boolean;
    usageResult: EnergyUsageOutput | undefined;
    errorMessage: string;
    isMobile: boolean;
    baseUsage: string;
};

// Split out pure component for testing.
export const EnergyBarView: React.FC<EnergyBarViewProps> = ({
    setBasicInfoExpand,
    inputComplete,
    loading,
    usageResult,
    errorMessage,
    isMobile,
    baseUsage,
}) => {
    const [addCleanHeatAndAc] = useAtom(addCleanHeatAndAcAtom);

    const [addHeatPumpWaterHeater] = useAtom(addHeatPumpWaterHeaterAtom);
    const [evs] = useAtom(evsAtom);

    const [addInductionStove] = useAtom(addInductionStoveAtom);
    const [addPeople] = useAtom(addPeopleAtom);
    const [addHvacUse] = useAtom(addHvacUseAtom);

    if (!inputComplete) {
        return (
            <div>
                Please enter{' '}
                <Anchor
                    className={styles.infoLink}
                    onClick={() => setBasicInfoExpand((v) => !v)}
                >
                    your baseline information
                </Anchor>{' '}
                to make calculations
            </div>
        );
    }

    if (loading) {
        return (
            <div>
                <Loader type="dots" size={30} color="#1f2674" />
            </div>
        );
    }

    if (errorMessage) {
        return <div>{errorMessage}</div>;
    }

    const showFutureUsage =
        addPeople.length ||
        addCleanHeatAndAc ||
        addHeatPumpWaterHeater ||
        addInductionStove ||
        addHvacUse ||
        evs.length > 0;

    const currentUsage = Number(baseUsage || 0);
    const hasFutureUsage = usageResult !== undefined;
    const usage = usageResult ?? {
        added_to_input: currentUsage,
        added_usage: 0,
    };

    const futureUsage = currentUsage + usage.added_to_input;
    const futureAdded = usage.added_usage;

    const showCurrentUsageFirst =
        currentUsage <= futureUsage || !hasFutureUsage;

    let currentPct = 0;
    let futurePct = 0;

    if (showCurrentUsageFirst) {
        [currentPct, futurePct] = adjustBarPcts(currentUsage, futureUsage);
    } else {
        [futurePct, currentPct] = adjustBarPcts(futureUsage, currentUsage);
    }

    const currentUsageStr = getDisplayNumber(currentUsage);
    const futureAddedStr = getSign(futureAdded) + getDisplayNumber(futureAdded);

    const currentLabel = (
        <div
            className={styles.currentLabel}
            style={{ width: `${currentPct}%` }}
        >
            {isMobile ? (
                <>
                    Current: {currentUsageStr} kWh
                    {showCurrentUsageFirst && SPACING}
                </>
            ) : (
                <>
                    Current Energy Usage: {currentUsageStr} kWh
                    {showCurrentUsageFirst && SPACING}
                </>
            )}
        </div>
    );

    const futureLabel = showFutureUsage ? (
        <div className={styles.futureLabel}>
            {isMobile ? (
                <>
                    Future: {futureAddedStr} kWh
                    {showCurrentUsageFirst || SPACING}
                </>
            ) : (
                <>
                    Potential future usage: {futureAddedStr} kWh
                    {showCurrentUsageFirst || SPACING}
                </>
            )}
        </div>
    ) : null;

    const currentBar = (
        <div
            className={styles.currentBar}
            style={{ width: `${currentPct}%` }}
        ></div>
    );

    const futureBar = showFutureUsage ? (
        <div className={styles.futureBar} style={{ width: `${futurePct}%` }} />
    ) : null;

    const deltaPct = Math.round((futureUsage / currentUsage - 1) * 100);

    const tooltipText =
        futureUsage === currentUsage ? (
            <>{currentUsageStr} kWh (no change)</>
        ) : (
            <>
                {showFutureUsage ? (
                    <>
                        {currentUsageStr} kWh → {getDisplayNumber(futureUsage)}{' '}
                        kWh ({getSign(deltaPct)}
                        {getDisplayNumber(deltaPct)}%)
                    </>
                ) : (
                    <>{currentUsageStr} kWh (no change)</>
                )}
            </>
        );

    return (
        <NoSSR>
            <div className={styles.wrapper}>
                <div className={styles.labels}>
                    {showCurrentUsageFirst ? (
                        <>
                            {currentLabel}
                            {hasFutureUsage && futureLabel}
                        </>
                    ) : (
                        <>
                            {futureLabel}
                            {currentLabel}
                        </>
                    )}
                </div>
                <AccessibleTooltip label={tooltipText}>
                    <div className={styles.bars}>
                        {showCurrentUsageFirst ? (
                            <>
                                <div className={styles.emptyBar} />
                                {hasFutureUsage && futureBar}
                                {currentBar}
                            </>
                        ) : (
                            <>
                                <div className={styles.emptyBar} />
                                {currentBar}
                                {hasFutureUsage && futureBar}
                            </>
                        )}
                    </div>
                </AccessibleTooltip>
            </div>
        </NoSSR>
    );
};
